import { useQuery } from "@tanstack/react-query";
import api, { inr2 } from "@/lib/api";
import { PageHeader, Panel, Loading, Pill } from "@/components/kit";
import { CheckCircle2, XCircle, BadgeCheck } from "lucide-react";

export default function Acceptance() {
  const { data, isLoading } = useQuery({
    queryKey: ["acceptance"],
    queryFn: async () => (await api.get("/acceptance-tests")).data,
  });
  if (isLoading || !data) return <Loading label="Running acceptance tests…" />;

  return (
    <div>
      <PageHeader title="Acceptance Tests" subtitle="Exact numeric checks for all three calculation engines (code-computed)" />
      <div className={`rounded-xl p-5 mb-6 flex items-center gap-4 ${data.all_passed ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"}`}
        data-testid="acceptance-summary">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white ${data.all_passed ? "bg-emerald-600" : "bg-red-600"}`}>
          <BadgeCheck size={24} />
        </div>
        <div>
          <div className="font-heading font-bold text-lg text-slate-900">
            {data.all_passed ? "All acceptance tests passed" : "Some tests failed"}
          </div>
          <div className="text-sm text-slate-500">{data.cases.filter((c) => c.passed).length} / {data.cases.length} numeric checks matching expected values</div>
        </div>
      </div>

      <Panel testid="acceptance-panel">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-slate-400 text-xs uppercase tracking-wider border-b border-slate-100">
                <th className="py-2.5 pr-4">Case</th><th className="py-2.5 pr-4">Engine</th>
                <th className="py-2.5 pr-4 text-right">Expected</th><th className="py-2.5 pr-4 text-right">Actual</th>
                <th className="py-2.5 text-right">Result</th>
              </tr>
            </thead>
            <tbody>
              {data.cases.map((c) => (
                <tr key={c.id} className="drill-row border-b border-slate-50" data-testid={`acceptance-${c.id}`}>
                  <td className="py-3 pr-4">
                    <div className="font-medium text-slate-800">{c.name}</div>
                    <div className="text-xs text-slate-400 font-mono">{c.id}</div>
                  </td>
                  <td className="py-3 pr-4"><Pill>{c.engine}</Pill></td>
                  <td className="py-3 pr-4 text-right font-mono text-slate-600">{inr2(c.expected)}</td>
                  <td className="py-3 pr-4 text-right font-mono font-semibold text-[#00529B]">{inr2(c.actual)}</td>
                  <td className="py-3 text-right">
                    {c.passed
                      ? <span className="inline-flex items-center gap-1 text-emerald-600 font-medium"><CheckCircle2 size={16} /> Pass</span>
                      : <span className="inline-flex items-center gap-1 text-red-600 font-medium"><XCircle size={16} /> Fail</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </div>
  );
}
